import { fetchWithTimeout } from '../../utils/fetchWithTimeout';
import CONSTANTS from '../../constants';

/**
 * Response type from the /real/latestDate endpoint
 */
export interface LatestDateResponse {
    success: boolean;
    latest_date?: string;           // Raw ISO date string from Solr
    formatted_date?: string;        // "yyyy-MM-dd" format
    total_documents?: number;       // Count of matching documents
    error?: string;
}

/**
 * Fetches the latest date that has data for the given facet filters
 *
 * @param facetSelected - Array of selected facets in "value@facetName" format
 * @param freeTextSearch - Optional text search query
 * @param signal - Optional AbortSignal for cancelling the request
 * @returns Promise with the latest date information
 *
 * @example
 * const result = await fetchLatestDate(['cryosat-2@mission_ss', 'GOM@producttype_ss']);
 * // Returns: { success: true, formatted_date: '2024-03-15', total_documents: 42 }
 */
export const fetchLatestDate = async (
    facetSelected: string[],
    freeTextSearch?: string,
    signal?: AbortSignal
): Promise<LatestDateResponse> => {
    try {
        // Build the selectedFacets query parameter
        // Backend expects format: "fieldName:value;fieldName2:value2"
        const facetParams = facetSelected
            .map(facet => {
                const parts = facet.split('@');
                if (parts.length === 2) {
                    // Format: "value@facetName" -> "facetName:value"
                    return `${parts[1]}:${parts[0]}`;
                }
                return null;
            })
            .filter(Boolean)
            .join(';');

        // Build query string
        let queryString = '';

        if (facetParams) {
            queryString += `selectedFacets=${encodeURIComponent(facetParams)}`;
        }

        if (freeTextSearch?.trim()) {
            queryString += `${queryString ? '&' : ''}q=${encodeURIComponent(freeTextSearch)}`;
        }

        // Add operator (using default from constants)
        queryString += `${queryString ? '&' : ''}op=${CONSTANTS.DEFAULT_OPERATOR_TO_USE}`;

        const url = `${CONSTANTS.ENDPOINT.REST_API}${CONSTANTS.REST_GET_LATEST_DATE}?${queryString}`;

        // Use signal if provided (for cancellation support), otherwise use default timeout
        const response = signal
            ? await fetch(url, { signal })
            : await fetchWithTimeout(url);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Check for backend error response
        if (data.ERROR) {
            console.error('[latestDateApi] Backend error:', data.ERROR);
            return {
                success: false,
                error: data.ERROR
            };
        }

        return data as LatestDateResponse;

    } catch (error) {
        // Handle abort specifically (don't log as error)
        if (error instanceof Error && error.name === 'AbortError') {
            console.log('[latestDateApi] Request was cancelled');
            return {
                success: false,
                error: 'Request cancelled'
            };
        }

        console.error('[latestDateApi] Error fetching latest date:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
        };
    }
};
